import { useState, forwardRef, useImperativeHandle, useCallback } from 'react';
import {
  useProjectPhotos,
  SelectablePhotoGallery,
  PhotoLightbox,
  unassignPhotosFromProject,
  updatePhotoDescription,
} from '@/features/photos';
import { useEffectiveBusinessId } from '@/hooks';
import type { ProjectPhotoView } from '@/features/photos';

interface ProjectPhotoGalleryProps {
  projectId: string;
}

export interface ProjectPhotoGalleryRef {
  refetch: () => Promise<void>;
}

export const ProjectPhotoGallery = forwardRef<ProjectPhotoGalleryRef, ProjectPhotoGalleryProps>(
  function ProjectPhotoGallery({ projectId }, ref) {
    const effectiveBusinessId = useEffectiveBusinessId();
    const { photos, isLoading, error, refetch } = useProjectPhotos({ projectId });
    
    const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number | null>(null);
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [isRemoving, setIsRemoving] = useState(false);
    const [removeError, setRemoveError] = useState<string | null>(null);

    // Expose refetch to parent
    useImperativeHandle(ref, () => ({
      refetch,
    }));

    const handlePhotoClick = (photo: ProjectPhotoView) => {
      const index = photos.findIndex((p) => p.id === photo.id);
      setSelectedPhotoIndex(index);
    };

    const handleCloseLightbox = () => {
      setSelectedPhotoIndex(null);
    };

    const handlePrevious = () => {
      if (selectedPhotoIndex !== null && selectedPhotoIndex > 0) {
        setSelectedPhotoIndex(selectedPhotoIndex - 1);
      }
    };

    const handleNext = () => {
      if (selectedPhotoIndex !== null && selectedPhotoIndex < photos.length - 1) {
        setSelectedPhotoIndex(selectedPhotoIndex + 1);
      }
    };

    const handleEnterSelectionMode = () => {
      setIsSelectionMode(true);
      setSelectedIds(new Set());
      setRemoveError(null);
    };

    const handleExitSelectionMode = () => {
      setIsSelectionMode(false);
      setSelectedIds(new Set());
      setRemoveError(null);
    };

    const handleDescriptionChange = useCallback(
      async (description: string) => {
        if (selectedPhotoIndex === null || !photos[selectedPhotoIndex]) return;

        const photo = photos[selectedPhotoIndex];
        try {
          await updatePhotoDescription(photo.id, description);
          await refetch();
        } catch (err) {
          console.error('Error updating photo description:', err);
        }
      },
      [selectedPhotoIndex, photos, refetch]
    );

    const handleRemoveFromProject = async () => {
      if (!effectiveBusinessId || selectedIds.size === 0) return;

      setIsRemoving(true);
      setRemoveError(null);

      try {
        const result = await unassignPhotosFromProject(
          Array.from(selectedIds),
          effectiveBusinessId
        );

        if (result.failed.length > 0) {
          setRemoveError(
            `Failed to remove ${result.failed.length} photo${result.failed.length !== 1 ? 's' : ''}`
          );
        }

        // Exit selection mode and refresh
        setIsSelectionMode(false);
        setSelectedIds(new Set());
        await refetch();
      } catch (err) {
        console.error('Error removing photos:', err);
        setRemoveError(err instanceof Error ? err.message : 'Failed to remove photos');
      } finally {
        setIsRemoving(false);
      }
    };

    if (isLoading) {
      return (
        <div className="animate-pulse space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="aspect-square bg-secondary-200 rounded-lg" />
            ))}
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error}</p>
          <button
            onClick={refetch}
            className="mt-2 text-sm text-red-600 underline hover:no-underline"
          >
            Try again
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {/* Selection Mode Controls */}
        {photos.length > 0 && (
          <div className="flex items-center justify-between">
            {isSelectionMode ? (
              <>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleExitSelectionMode}
                    className="text-sm text-secondary-600 hover:text-secondary-800 font-medium"
                  >
                    Cancel
                  </button>
                </div>
                <button
                  onClick={handleRemoveFromProject}
                  disabled={selectedIds.size === 0 || isRemoving}
                  className={`
                    flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors
                    ${selectedIds.size === 0 || isRemoving
                      ? 'bg-secondary-100 text-secondary-400 cursor-not-allowed'
                      : 'bg-red-50 text-red-700 hover:bg-red-100'
                    }
                  `}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                  {isRemoving
                    ? 'Removing...'
                    : `Remove ${selectedIds.size > 0 ? `(${selectedIds.size})` : ''}`}
                </button>
              </>
            ) : (
              <div className="flex items-center justify-between w-full">
                <span className="text-sm text-secondary-500">
                  {photos.length} photo{photos.length !== 1 ? 's' : ''}
                </span>
                <button
                  onClick={handleEnterSelectionMode}
                  className="text-sm text-secondary-600 hover:text-secondary-800 font-medium"
                >
                  Remove Photos
                </button>
              </div>
            )}
          </div>
        )}

        {/* Error Message */}
        {removeError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-700">{removeError}</p>
          </div>
        )}

        {/* Gallery */}
        <SelectablePhotoGallery
          photos={photos}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          onPhotoClick={handlePhotoClick}
          isSelectionMode={isSelectionMode}
          emptyMessage="No photos have been added to this project yet"
        />

        {/* Lightbox */}
        {selectedPhotoIndex !== null && photos[selectedPhotoIndex] && !isSelectionMode && (
          <PhotoLightbox
            photo={photos[selectedPhotoIndex]}
            onClose={handleCloseLightbox}
            onPrevious={handlePrevious}
            onNext={handleNext}
            hasPrevious={selectedPhotoIndex > 0}
            hasNext={selectedPhotoIndex < photos.length - 1}
            onDescriptionChange={handleDescriptionChange}
          />
        )}
      </div>
    );
  }
);